import {
  ICreateDepartmentFormData,
  ICreateDepartmentResponse,
  IDepartmentsResponse,
  IDepartmentView,
  IGetDepartmentFormDefaultsParams,
  IHandleCreateDepartmentParams,
  IHandleDeleteDepartmentParams,
  IHandleCreateOrSaveDepartmentParams,
  IHandleGetDepartmentsParams,
  IHandleUpdateDepartmentParams,
  IUpdateDepartmentResponse,
} from "@model/Department.model";
import { AxiosCalls } from "./Functions/AxiosCalls";
import { handleDefaultApiErrors } from "Utils/Global/apiErrorHandling";

export class DepartmentsService {
  async handleGetAllDepartments({
    setLoading,
    setApiError,
    setItems,
    setTotal,
    token,
    onAuthError,
    onForbidden,
  }: IHandleGetDepartmentsParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Get<IDepartmentsResponse>(
        "/department/all-departments",
        token
      );

      const payload = resp?.data ?? {};
      const itemsRaw: IDepartmentView[] = payload?.items ?? [];

      setItems(itemsRaw);

      const total =
        typeof payload?.meta?.total === "number"
          ? payload.meta.total
          : itemsRaw.length;

      setTotal(total);
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden,
      });
      setApiError(msg);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  searchDepartmentsLocal(args: {
    allDepartments: IDepartmentView[];
    search: string;
  }): {
    items: IDepartmentView[];
    total: number;
    subtitle: string;
    emptyMessage: string;
  } {
    const { allDepartments, search } = args;
    const query = search.trim().toLowerCase();

    if (!query) {
      const total = allDepartments.length;
      return {
        items: allDepartments,
        total,
        subtitle: `Total: ${total}`,
        emptyMessage: "No departments found.",
      };
    }

    const filtered = allDepartments.filter((department) => {
      const name = department.name.toLowerCase();
      const description = (department.description ?? "").toLowerCase();
      const headFirst = (
        department.headOfDepartmentFirstName ?? ""
      ).toLowerCase();
      const headLast = (
        department.headOfDepartmentLastName ?? ""
      ).toLowerCase();

      return (
        name.includes(query) ||
        description.includes(query) ||
        headFirst.includes(query) ||
        headLast.includes(query)
      );
    });

    const total = filtered.length;

    return {
      items: filtered,
      total,
      subtitle: `Showing ${total} result(s) for “${search}”`,
      emptyMessage: total === 0 ? `No departments found for “${search}”.` : "",
    };
  }

  async handleCreateDepartment({
    data,
    setLoading,
    setApiError,
    token,
    reset,
    onAuthError,
    onForbidden
  }: IHandleCreateDepartmentParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Post<
        ICreateDepartmentResponse,
        ICreateDepartmentFormData
      >("/department/add-department", data, token);

      reset();
      return resp?.data?.message ?? "Department created successfully.";
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden

      });
      setApiError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async handleDeleteDepartment({
    id,
    token,
    setLoading,
    setApiError,
    onSuccess,
    onAuthError,
    onForbidden
  }: IHandleDeleteDepartmentParams): Promise<string | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Delete<{ message: string }>(
        `/department/deletedepartments/${id}`,
        token
      );

      const message =
        resp?.data?.message ?? "Department deleted successfully.";
      if (onSuccess) onSuccess(message);
      return message;
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden
      });
      setApiError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  async handleUpdateDepartment({
    id,
    data,
    setLoading,
    setApiError,
    token,
    onAuthError,
    onForbidden
  }: IHandleUpdateDepartmentParams): Promise<{
    message: string;
    department: IDepartmentView;
  } | null> {
    setLoading(true);
    setApiError(null);

    try {
      const resp = await AxiosCalls.Put<
        IUpdateDepartmentResponse,
        Partial<ICreateDepartmentFormData>
      >(`/department/update-department/${id}`, data, token);

      const payload = resp?.data;
      const updated = payload?.department?.item;

      if (!updated) {
        setApiError("Invalid response from server.");
        return null;
      }

      return {
        message: payload?.message ?? "Department updated successfully.",
        department: updated,
      };
    } catch (err) {
      const msg = handleDefaultApiErrors(err, {
        hadToken: Boolean(token),
        onAuthError,
        onForbidden
      });
      setApiError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }

  getFormDefaultsFromDepartment({
    initialDepartment,
    defaultValues,
  }: IGetDepartmentFormDefaultsParams): ICreateDepartmentFormData {
    if (!initialDepartment) {
      return {
        ...defaultValues,
      };
    }

    return {
      ...defaultValues,
      name: initialDepartment.name,
      description: initialDepartment.description ?? "",
      headOfDepartmentUserId: initialDepartment.headOfDepartmentUserId ?? "",
    };
  }

  async handleCreateOrSaveDepartment({
    isEdit,
    initialDepartment,
    values,
    token,
    setLoading,
    setApiError,
    reset,
    onSuccess,
    onClose,
    onAuthError,
    onForbidden,
  }: IHandleCreateOrSaveDepartmentParams): Promise<void> {
    if (isEdit && initialDepartment) {
      const result = await this.handleUpdateDepartment({
        id: initialDepartment.id,
        data: values,
        token,
        setApiError,
        setLoading,
        onAuthError,
        onForbidden
      });

      if (!result) return;

      if (onSuccess) {
        onSuccess(result.department);
      }

      onClose();
      return;
    }

    const msg = await this.handleCreateDepartment({
      data: values,
      reset,
      token,
      setApiError,
      setLoading,
      onAuthError,
      onForbidden
    });

    if (!msg) return;

    onClose();

    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }

  findById(list: IDepartmentView[], id: string): IDepartmentView | null {
    return list.find((d) => d.id === id) ?? null;
  }

  addOrUpdateDepartmentInList(
    list: IDepartmentView[],
    updated: IDepartmentView
  ): IDepartmentView[] {
    const exists = list.some((d) => d.id === updated.id);
    if (!exists) {
      return [...list, updated];
    }
    return list.map((d) => (d.id === updated.id ? updated : d));
  }
}
